// Quelle: /opt/lilasp-zeitplan/frontend/src/lib/code.ts (Stand 2026-09-29)
// ============================================================
// Die sechs Ziffernkästchen der Anmeldung.
//
// Die ganze Logik steht als reine Funktion hier und nicht im Bildschirm:
// Einfügen aus der Zwischenablage, Autofill des Einmalcodes vom Telefon,
// Rücktaste über Kästchengrenzen — das sind genau die Fälle, die man im
// Browser nur mühsam von Hand durchspielt und hier in Sekunden prüft.
// ============================================================

export const CODE_LAENGE = 6;

/** Ein leeres Feld — sechs leere Kästchen. */
export function leererCode(): string[] {
  return Array.from({ length: CODE_LAENGE }, () => "");
}

/** Nur die Ziffern aus einer Eingabe. Alles andere (Leerzeichen aus dem
 *  Einfügen, Bindestriche aus der Mail) fliegt raus. */
export function ziffernAus(roh: string): string {
  return (roh.match(/\d/g) ?? []).join("");
}

export interface Codestand {
  code: string[];
  /** Kästchen, das danach den Fokus bekommen soll */
  fokus: number;
}

/**
 * Eingabe in Kästchen `index`. Eine einzelne Ziffer rückt um eins weiter;
 * ein eingefügter oder automatisch ausgefüllter Code verteilt sich ab
 * `index` über die folgenden Kästchen — genau das macht iOS beim Antippen
 * des Codevorschlags, und ohne diese Verteilung landeten alle sechs Ziffern
 * in einem Kästchen.
 */
export function codeSetzen(alt: readonly string[], index: number, roh: string): Codestand {
  const code = [...alt];
  const ziffern = ziffernAus(roh);
  if (!ziffern) {
    code[index] = "";
    return { code, fokus: index };
  }
  let i = index;
  for (const z of ziffern) {
    if (i >= CODE_LAENGE) break;
    code[i] = z;
    i += 1;
  }
  return { code, fokus: Math.min(i, CODE_LAENGE - 1) };
}

/**
 * Rücktaste. Steht etwas im Kästchen, wird es geleert und der Fokus bleibt;
 * ist es leer, geht es ein Kästchen zurück und leert dort. Das ist die
 * Bewegung, die man von Codefeldern kennt — alles andere fühlt sich klemmig an.
 */
export function codeRuecktaste(alt: readonly string[], index: number): Codestand {
  const code = [...alt];
  if (code[index]) {
    code[index] = "";
    return { code, fokus: index };
  }
  const vorher = Math.max(0, index - 1);
  code[vorher] = "";
  return { code, fokus: vorher };
}

export function codeVollstaendig(code: readonly string[]): boolean {
  return code.length === CODE_LAENGE && code.every((z) => /^\d$/.test(z));
}

export function codeText(code: readonly string[]): string {
  return code.join("");
}
